import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';

export interface Project {
  id: number;
  clientId: number;
  projectName: string;
  projectDescription?: string;
  projectStatusId?: number;
  projectStartDate?: string;
  projectEndDate?: string;
  projectBudget?: number;
  projectNotes?: string;
  defaultAgreementId?: number;
  createdAt?: string;
  createdById?: number;
  updatedAt?: string;
  updatedById?: number;
  taskCount?: number;
  // Related data from backend
  client?: {
    clientId: number;
    clientName?: string;
  };
  projectStatus?: {
    id: number;
    ps_descr?: string;
  };
  defaultAgreement?: {
    id: number;
    description?: string;
    agreementType?: string;
  };
  tasks?: Array<{
    taskId: number;
    taskDescription?: string;
    taskStatus?: number;
  }>;
}

export interface ProjectsResponse {
  records: Project[];
}

export const useProjects = (searchParams?: string, filter?: string) => {
  let queryString = '';
  if (searchParams || filter) {
    const params = new URLSearchParams();
    if (searchParams) params.append('searchParams', searchParams);
    if (filter) params.append('filter', filter);
    queryString = `?${params.toString()}`;
  }

  const { data, error, isLoading, mutate } = useSWR<ProjectsResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data?.records || [],
    projects: data?.records || [],
    total: data?.records?.length || 0,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useProject = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Project>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data,
    project: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();
  
  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get support agreements for select dropdown
export const useSupportAgreementsForSelect = (clientId?: number) => {
  const { data, error, isLoading } = useSWR<any>(
    clientId ? `/crm-support-agreements?clientId=${clientId}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    agreements: data?.records || [],
    isLoading,
    isError: error,
    options: (data?.records || []).map((agreement: any) => ({
      value: String(agreement.id),
      label: agreement.description || `Agreement #${agreement.id}`,
    })),
  };
};

// Hook to get project statuses for select dropdown
export const useProjectStatusesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-project-status',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    statuses: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((status: any) => ({
      value: String(status.id),
      label: status.ps_descr || `Status #${status.id}`,
    })),
  };
};
